import React, { useState } from "react"
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { Flex, Form, Input } from "antd"
import { Button } from "../../components/Button"
import { useTranslation } from "react-i18next"
import {
  CRYPTO_NEWS_URL,
  FT_NEWS_URL,
  STORAGE_ENCRYPTED_DATA,
  STORAGE_IV,
  WEB_PORTALS,
} from "../../constants"
import { copyArticle } from "../../services/copy"
import { ContentWrapper } from "../../components/Wrappers"
import { getStorageVariable } from "../../utils/storage"
import { ProgressBar } from "../../components/ProgressBar"
import { Text } from "../../components/Text"

const { slovakCryptoNews, czechCryptoNews, slovakFtNews, czechFtNews } =
  WEB_PORTALS

export const CopyArticles: React.FC = () => {
  const { t } = useTranslation()
  const [messages, setMessages] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [urls, setUrls] = useState<string[]>([])
  const [form] = Form.useForm()

  const numberOfFTNArticles = urls.filter((item: string | undefined) =>
    item?.includes(FT_NEWS_URL)
  ).length
  const numberOfKNArticles = urls.filter((item: string | undefined) =>
    item?.includes(CRYPTO_NEWS_URL)
  ).length
  const numberOfArticles = numberOfFTNArticles + numberOfKNArticles

  const noValue = numberOfArticles === 0

  const removeFinishedUrl = (finishedUrl: string) => {
    const currentUrls: string[] = form.getFieldValue("urls") || []
    const newUrls = currentUrls.filter((url) => url !== finishedUrl)
    if (newUrls.length === 0) newUrls.push("")
    form.setFieldsValue({ urls: newUrls })
    setUrls(form.getFieldValue("urls"))
  }

  const onFinish = async (values: { urls: [string] }) => {
    setIsLoading(true)
    console.log("Received values of form:", values)
    for (const url of values.urls) {
      const loginData = url.includes(FT_NEWS_URL)
        ? {
            slovakData: getStorageVariable(
              `${slovakFtNews}${STORAGE_ENCRYPTED_DATA}`
            ),
            slovakIv: getStorageVariable(`${slovakFtNews}${STORAGE_IV}`),
            czechData: getStorageVariable(
              `${czechFtNews}${STORAGE_ENCRYPTED_DATA}`
            ),
            czechIv: getStorageVariable(`${czechFtNews}${STORAGE_IV}`),
          }
        : {
            slovakData: getStorageVariable(
              `${slovakCryptoNews}${STORAGE_ENCRYPTED_DATA}`
            ),
            slovakIv: getStorageVariable(`${slovakCryptoNews}${STORAGE_IV}`),
            czechData: getStorageVariable(
              `${czechCryptoNews}${STORAGE_ENCRYPTED_DATA}`
            ),
            czechIv: getStorageVariable(`${czechCryptoNews}${STORAGE_IV}`),
          }
      console.log(`Sending request for: ${url}`)
      await copyArticle(url, loginData, (partialMessage) => {
        setMessages((prev) => [...prev, partialMessage])
        try {
          const parsedMsg = JSON.parse(partialMessage)
          // Check if the process finished successfully.
          if (parsedMsg.data && parsedMsg.data.progress === 100) {
            // Determine which URL to remove.
            const finishedUrl =
              parsedMsg.data.originalArticleUrl || parsedMsg.data.url
            removeFinishedUrl(finishedUrl)
          }
        } catch (error) {
          console.error("Error parsing message:", error)
        }
      })
    }
    setIsLoading(false)
  }

  const processingData = messages.map((msg, index) => {
    const parsedMsg = JSON.parse(msg)
    return parsedMsg
  })

  const transformData = (arr: any[]) => {
    const result = []
    let group = []
    let currentHeader = null

    for (let item of arr) {
      // Check if the item has a header in its data property.
      if (item.data && item.data.originalArticleUrl) {
        // If we're starting a new header group, flush the old one.
        if (
          currentHeader === null ||
          currentHeader !== item.data.originalArticleUrl
        ) {
          if (group.length > 0) {
            result.push(group)
            group = []
          }
          currentHeader = item.data.originalArticleUrl
        }
        // Add the current item to the group.
        group.push(item)
      } else {
        // When an item does not have a header (e.g. "started" or "finished")
        // flush any existing header group first.
        if (group.length > 0) {
          result.push(group)
          group = []
          currentHeader = null
        }
        // Add the non-header item directly to the result.
        result.push(item)
      }
    }

    // If there's a remaining group, add it.
    if (group.length > 0) {
      result.push(group)
    }

    return result
  }

  const transformedData = transformData(processingData)

  const articleProcesses = transformedData.filter((item) => Array.isArray(item))

  const mappedArticleProcesses = articleProcesses.map((item) => {
    const lastItem = item.at(-1)
    const {
      message,
      data: { header, progress, url, czHeader, czUrl, isWrong },
    } = lastItem
    return (
      <ProgressBar
        progress={progress}
        message={message}
        process="copying"
        url={url}
        articleName={header}
        czUrl={czUrl}
        czArticleName={czHeader}
        isTextWrong={isWrong}
      />
    )
  })

  return (
    <ContentWrapper>
      <Form
        form={form}
        name="dynamic_form_item"
        onFinish={onFinish}
        layout="vertical"
        initialValues={{
          urls: [""],
        }}
      >
        <Form.List name="urls">
          {(fields, { add, remove }, { errors }) => (
            <>
              {fields.map((field, index) => (
                <Form.Item
                  label={index === 0 ? t("copying.urlFieldName") : ""}
                  required={false}
                  key={field.key}
                >
                  <Form.Item
                    {...field}
                    validateTrigger={["onChange", "onBlur"]}
                    rules={[
                      {},
                      {
                        validator: (_, value) =>
                          new Promise<void>((resolve, reject) => {
                            setTimeout(() => {
                              if (
                                value &&
                                (value.startsWith(CRYPTO_NEWS_URL) ||
                                  value.startsWith(FT_NEWS_URL))
                              ) {
                                const occurrences = urls.filter(
                                  (url) => url === value
                                ).length

                                if (occurrences > 1) {
                                  reject(
                                    new Error(t("copying.duplicatedUrlErrMsg"))
                                  )
                                } else {
                                  resolve()
                                }
                              } else {
                                reject(new Error(t("copying.wrongUrlErrMsg")))
                              }
                            }, 500)
                          }),
                      },
                    ]}
                    noStyle
                  >
                    <Input
                      readOnly={isLoading}
                      placeholder={t("copying.urlFieldPlaceholder")}
                      onChange={() => {
                        setUrls(form.getFieldValue("urls"))
                      }}
                      suffix={
                        fields.length > 1 ? (
                          <DeleteOutlined
                            className="dynamic-delete-button"
                            style={{ cursor: isLoading ? "not-allowed" : "" }}
                            onClick={() => {
                              if (!isLoading) {
                                remove(field.name)
                                setUrls(form.getFieldValue("urls"))
                              }
                            }}
                          />
                        ) : null
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Tab" && !isLoading) {
                          e.preventDefault()
                          add()
                          setUrls(form.getFieldValue("urls"))
                          setTimeout(() => {
                            const newIndex = fields.length
                            const newInput = document.getElementById(
                              `dynamic_form_item_urls_${newIndex}`
                            ) as HTMLInputElement | null
                            if (newInput) {
                              newInput.focus()
                            }
                          }, 0)
                        }
                      }}
                    />
                  </Form.Item>
                </Form.Item>
              ))}
              <Text
                children={`Kryptonovinky (${numberOfKNArticles}), FT news (${numberOfFTNArticles})`}
                type="secondary"
              />
              <Flex justify="center">
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => {
                      if (!isLoading) {
                        add()
                        setUrls(form.getFieldValue("urls"))
                      }
                    }}
                    style={{ width: "300px" }}
                    icon={<PlusOutlined />}
                    label={t("copying.addBtn")}
                    disabled={isLoading}
                  />
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </Flex>
            </>
          )}
        </Form.List>
        <Flex justify="center">
          <Form.Item>
            <Button
              type="primary"
              label={
                isLoading
                  ? `${t("copying.copyingArticles")} (${numberOfArticles})`
                  : `${t("copying.copyArticles")} (${numberOfArticles})`
              }
              htmlType="submit"
              style={{ width: "300px" }}
              loading={isLoading}
              disabled={noValue}
            />
          </Form.Item>
        </Flex>
      </Form>
      {mappedArticleProcesses}
    </ContentWrapper>
  )
}
